import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export type PlanProposal = {
  title?: string;
  chart_family?: string;
  brief?: string;
  mappings_brief?: Record<string, unknown>;
};

export function ModifyPlanDialog(props: {
  plan: any;
  open: boolean;
  onClose: () => void;
  onSubmit: (rawText: string) => Promise<void>;
}) {
  const { plan, open, onClose, onSubmit } = props;
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const limit = 1000;
  const remaining = useMemo(() => Math.max(0, limit - text.length), [text]);

  const canned = [
    'Use log scale',
    'More bins',
    'Swap axes',
    'Facet by category',
    'Color by group'
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Tell Lumi how to change this plan</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            Your feedback guides Lumi. The more specific, the better it aligns to your needs.
          </div>
          <div>
            <textarea
              value={text}
              onChange={(e) => {
                const v = e.target.value;
                setText(v.length > limit ? v.slice(0, limit) : v);
              }}
              rows={6}
              className="w-full border border-border rounded-md p-2 text-sm bg-background"
              placeholder="e.g., Use violin plot, facet by Species, log Y axis"
            />
            <div className="mt-1 text-[11px] text-muted-foreground text-right">
              {remaining} characters remaining
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canned.map((label) => (
              <Button
                key={label}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const next = (text ? text + (text.endsWith('\n') ? '' : ' ') : '') + label;
                  setText(next.slice(0, limit));
                }}
              >
                {label}
              </Button>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!text.trim()) return;
                setSubmitting(true);
                const p = onSubmit(text.trim());
                // Close immediately for better UX; do not wait for network
                onClose();
                try { await p; } finally { setSubmitting(false); }
              }}
              disabled={submitting || !text.trim()}
            >
              {submitting ? 'Updating…' : 'Update plan'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ModifyPlanDialog;


